import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
// @mui
import {
  Box,
  List,
  Badge,
  Button,
  Tooltip,
  Divider,
  Popover,
  Typography,
  IconButton,
  ListItemText,
  ListItemButton,
  CircularProgress,
} from "@mui/material";
// icons
import NotificationsIcon        from "@mui/icons-material/NotificationsOutlined";
import DoneAllIcon               from "@mui/icons-material/DoneAll";
import AssignmentTurnedInIcon    from "@mui/icons-material/AssignmentTurnedIn";
import AssignmentLateIcon        from "@mui/icons-material/AssignmentLate";
import AssignmentIcon            from "@mui/icons-material/Assignment";
import FireplaceIcon             from "@mui/icons-material/LocalFireDepartment";
import InventoryIcon             from "@mui/icons-material/Inventory2Outlined";
import ReportProblemIcon         from "@mui/icons-material/ReportProblem";
import StoreIcon                 from "@mui/icons-material/Store";
import VerifiedIcon              from "@mui/icons-material/VerifiedOutlined";
import SettingsIcon              from "@mui/icons-material/TuneOutlined";
import { formatDistanceToNow }   from "date-fns";
// utils
import Scrollbar from "../../../components/scrollbar";
// CR-2026-FE-CI-BUILD-FIX — moved to top to satisfy eslint import/first.
// Was previously declared mid-file at line 63, which broke `react-scripts build`
// and silently failed every Deploy Frontend to Production run since PR #6.
// Used by authHeaders() below — centralised authToken module so a future
// cookie-based auth migration is a one-file change.
import { getAccessToken } from "../../../utils/authToken";

// ─── Config ──────────────────────────────────────────────────────────────────

const API_BASE    = "/api/notifications";
const POLL_MS     = 30_000;   // 30-second unread-count poll
const MAX_DISPLAY = 15;

// ─── Event type → icon map ────────────────────────────────────────────────────

function eventIcon(type) {
  const map = {
    "task.assigned":         <AssignmentIcon       sx={{ fontSize: 20, color: "info.main" }} />,
    "task.approved":         <AssignmentTurnedInIcon sx={{ fontSize: 20, color: "success.main" }} />,
    "task.rejected":         <AssignmentLateIcon   sx={{ fontSize: 20, color: "error.main" }} />,
    "task.completed":        <AssignmentTurnedInIcon sx={{ fontSize: 20, color: "success.dark" }} />,
    "heat.released":         <FireplaceIcon         sx={{ fontSize: 20, color: "warning.main" }} />,
    "heat.cmtr_verified":    <VerifiedIcon          sx={{ fontSize: 20, color: "success.main" }} />,
    "release_package.activated": <InventoryIcon     sx={{ fontSize: 20, color: "primary.main" }} />,
    "ncr.opened":            <ReportProblemIcon     sx={{ fontSize: 20, color: "error.main" }} />,
    "ncr.closed":            <DoneAllIcon           sx={{ fontSize: 20, color: "success.main" }} />,
    "ncr.disposition_set":   <ReportProblemIcon     sx={{ fontSize: 20, color: "warning.dark" }} />,
    "vendor.approved":       <StoreIcon             sx={{ fontSize: 20, color: "success.main" }} />,
    "vendor.suspended":      <StoreIcon             sx={{ fontSize: 20, color: "error.main" }} />,
  };
  return map[type] || <NotificationsIcon sx={{ fontSize: 20, color: "text.secondary" }} />;
}

// ─── Auth header helper ───────────────────────────────────────────────────────
// (getAccessToken import moved to top of file per eslint import/first rule;
// see CR-2026-FE-CI-BUILD-FIX comment in the imports block above.)

function authHeaders() {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function NotificationsPopover() {
  const navigate = useNavigate();

  const [anchorEl,     setAnchorEl]     = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount,  setUnreadCount]  = useState(0);
  const [loading,      setLoading]      = useState(false);
  const pollRef = useRef(null);

  // ── fetch unread count (lightweight — used for badge polling) ──────────────
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/unread-count`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setUnreadCount(data?.data?.unreadCount ?? data?.unreadCount ?? 0);
    } catch {
      // swallow — no need to surface poll errors
    }
  }, []);

  // ── fetch full notification list (called on popover open) ─────────────────
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}?limit=${MAX_DISPLAY}`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const list = data?.data?.notifications ?? data?.notifications ?? [];
      setNotifications(list);
      // recount from fresh list
      setUnreadCount(list.filter((n) => !n.isRead).length);
    } catch {
      /* swallow */
    } finally {
      setLoading(false);
    }
  }, []);

  // ── polling on mount ──────────────────────────────────────────────────────
  useEffect(() => {
    fetchUnreadCount();
    pollRef.current = setInterval(fetchUnreadCount, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchUnreadCount]);

  // ── popover open/close ────────────────────────────────────────────────────
  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    fetchNotifications();
  };
  const handleClose = () => setAnchorEl(null);

  // ── mark single as read ───────────────────────────────────────────────────
  const markRead = useCallback(async (id, link) => {
    // optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
    );
    setUnreadCount((c) => Math.max(0, c - 1));

    try {
      await fetch(`${API_BASE}/${id}/read`, {
        method: "PATCH",
        headers: authHeaders(),
      });
    } catch { /* swallow */ }

    if (link) {
      handleClose();
      navigate(link);
    }
  }, [navigate]);

  // ── mark all as read ──────────────────────────────────────────────────────
  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
    try {
      await fetch(`${API_BASE}/read-all`, {
        method: "PATCH",
        headers: authHeaders(),
      });
    } catch { /* swallow */ }
  }, []);

  const open = Boolean(anchorEl);
  const unread  = notifications.filter((n) => !n.isRead);
  const earlier = notifications.filter((n) =>  n.isRead);

  return (
    <>
      {/* ── Bell button ── */}
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleOpen}
          sx={{ width: 40, height: 40, color: open ? "primary.main" : "text.primary" }}
        >
          <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* ── Dropdown panel ── */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { mt: 1.5, width: 380, borderRadius: 2, boxShadow: 6 } }}
      >
        {/* Header row */}
        <Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {unreadCount > 0
                ? `${unreadCount} unread`
                : "All caught up"}
            </Typography>
          </Box>

          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton color="primary" onClick={markAllRead} size="small">
                <DoneAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        {/* List */}
        <Scrollbar sx={{ maxHeight: 400 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <NotificationsIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No notifications yet
              </Typography>
            </Box>
          ) : (
            <>
              {/* Unread section */}
              {unread.length > 0 && (
                <List disablePadding>
                  <Typography
                    variant="overline"
                    sx={{ px: 2.5, pt: 1.5, pb: 0.5, display: "block", color: "text.secondary" }}
                  >
                    New
                  </Typography>
                  {unread.map((n) => (
                    <NotifItem key={n.id} notif={n} onRead={markRead} />
                  ))}
                </List>
              )}

              {/* Earlier section */}
              {earlier.length > 0 && (
                <List disablePadding>
                  <Typography
                    variant="overline"
                    sx={{ px: 2.5, pt: 1.5, pb: 0.5, display: "block", color: "text.secondary" }}
                  >
                    Earlier
                  </Typography>
                  {earlier.map((n) => (
                    <NotifItem key={n.id} notif={n} onRead={markRead} />
                  ))}
                </List>
              )}
            </>
          )}
        </Scrollbar>

        <Divider sx={{ borderStyle: "dashed" }} />

        {/* Footer */}
        <Box sx={{ p: 1, display: "flex", gap: 1 }}>
          <Button
            fullWidth
            size="small"
            color="inherit"
            onClick={() => { handleClose(); navigate("/settings/notifications"); }}
            startIcon={<SettingsIcon fontSize="small" />}
          >
            Notification Settings
          </Button>
        </Box>
      </Popover>
    </>
  );
}

// ─── NotifItem ────────────────────────────────────────────────────────────────

function NotifItem({ notif, onRead }) {
  const isUnread = !notif.isRead;

  return (
    <ListItemButton
      onClick={() => onRead(notif.id, notif.link || null)}
      sx={{
        py: 1.25,
        px: 2.5,
        alignItems: "flex-start",
        gap: 1.5,
        ...(isUnread && { bgcolor: "action.selected" }),
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          mt: 0.25,
          width: 36,
          height: 36,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          bgcolor: "background.neutral",
        }}
      >
        {eventIcon(notif.type)}
      </Box>

      {/* Text */}
      <ListItemText
        disableTypography
        primary={
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: isUnread ? 700 : 400,
              lineHeight: 1.35,
              mb: 0.25,
            }}
          >
            {notif.title || notif.message || "Notification"}
          </Typography>
        }
        secondary={
          <Typography variant="caption" color="text.disabled" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {notif.createdAt
              ? formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true })
              : ""}
          </Typography>
        }
      />

      {/* Unread dot */}
      {isUnread && (
        <Box
          sx={{
            mt: 1,
            ml: "auto",
            width: 8,
            height: 8,
            flexShrink: 0,
            borderRadius: "50%",
            bgcolor: "primary.main",
          }}
        />
      )}
    </ListItemButton>
  );
}
